import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IcpFormPreview from '../components/IcpFormPreview';
import OfferPageHero from '../components/OfferPageHero';
import PackagePicker from '../components/PackagePicker';
import { resolveFormVariant } from '../config/formVariants';
import { getIndustryProfile } from '../data/industryProfiles';
import { apiFetch } from '../services/api';
import './GenerateOffer.css';

// Registrar batches (GoDaddy per-domain, Namecheap up to ~15s) plus optional OpenAI
// often exceed 30s on kings.store and similar hosts; keep client abort above server work.
const OFFER_REQUEST_TIMEOUT_MS = 90000;

const GenerateOffer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [formData, setFormData] = useState({
    company: '',
    industry: '',
    location: '',
    email: '',
    website: '',
  });

  const [offerData, setOfferData] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [currentDomainIndex, setCurrentDomainIndex] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('domain_only');
  const formVariant = useMemo(
    () => resolveFormVariant(window.location.search, window.location.hostname),
    []
  );
  const queryIndustry = useMemo(
    () => new URLSearchParams(window.location.search).get('industry') || '',
    []
  );

  const resultsRef = useRef(null);

  // Scroll results into view when offer first loads
  useEffect(() => {
    if (offerData && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [offerData]);

  // Keyboard navigation for domain gallery (← / →)
  const handleGalleryKeyDown = useCallback(
    (e) => {
      if (!offerData) return;
      const avail = offerData.offer.candidates.filter(d => d.availability === 'available');
      const sorted = avail.length > 0 ? avail : offerData.offer.candidates;
      const total = sorted.length;
      if (total <= 1) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentDomainIndex(prev => (prev - 1 + total) % total);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentDomainIndex(prev => (prev + 1) % total);
      }
    },
    [offerData]
  );

  const industries = [
    'AI and ML',
    'SaaS',
    'Developer Tools',
    'Fintech',
    'Technology',
    'Marketing',
    'E-commerce',
    'Consulting',
    'Real Estate',
    'Medical',
    'Legal',
    'Construction',
    'Plumbing',
    'Dental',
    'HVAC',
    'Accounting',
    'Auto Repair',
    'Fitness',
    'Food & Beverage',
  ];

  useEffect(() => {
    if (!queryIndustry) return;
    setFormData(prev => (
      prev.industry
        ? prev
        : {
          ...prev,
          industry: queryIndustry,
        }
    ));
  }, [queryIndustry]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleGenerateOffer = async (e) => {
    e.preventDefault();

    if (!formData.company.trim()) {
      setError('Company name is required');
      return;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OFFER_REQUEST_TIMEOUT_MS);
    const requestBody = {
      company: formData.company,
      industry: formData.industry || undefined,
      location: formData.location || undefined,
      email: formData.email || undefined,
      website: formData.website || undefined,
      generate_pitch: true,
      send_email: false,
    };

    const requestOffer = (offline) => apiFetch('/api/offer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      body: JSON.stringify({
        ...requestBody,
        offline,
      }),
    });

    try {
      let data;

      try {
        data = await requestOffer(false);
      } catch (err) {
        const shouldFallbackToOffline = err?.status === 503 && (
          err?.message?.includes('Unable to secure any verified, available domains right now')
          || err?.data?.detail?.includes('Unable to secure any verified, available domains right now')
        );

        if (!shouldFallbackToOffline) {
          throw err;
        }

        data = await requestOffer(true);
        setSuccessMessage('Domains generated in preview mode while live verification is unavailable.');
      }

      if (!data.success) {
        throw new Error(data.detail || 'Failed to generate offer');
      }

      setOfferData(data);
      setSuccessMessage((prev) => prev || 'Domains generated successfully!');
      // Reset email state and domain index for new offer
      setEmailSent(false);
      setEmailError(null);
      setEmailCooldown(0);
      setCurrentDomainIndex(0);
    } catch (err) {
      console.error('Error generating offer:', err);
      const message = err?.name === 'AbortError' ? 'Request timed out. Please try again.' : err.message;
      setError(`Failed to generate offer: ${message}`);
      setOfferData(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!offerData?.offer) {
      setEmailError('No offer to send');
      return;
    }

    if (!formData.email) {
      setEmailError('Enter email above');
      return;
    }

    if (emailCooldown > 0) {
      setEmailError(`Wait ${emailCooldown}s before resending`);
      return;
    }

    setEmailSending(true);
    setEmailError(null);
    setEmailSent(false);

    try {
      const data = await apiFetch('/api/send-offer-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          lead_id: offerData.offer.lead.id,
          company: formData.company,
          candidates: offerData.offer.candidates,
        }),
      });

      if (!data.success) {
        throw new Error(data.detail || data.message || 'Send failed');
      }

      setEmailSent(true);
      // Rate limit: 60 second cooldown
      setEmailCooldown(60);
      const timer = setInterval(() => {
        setEmailCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  const handleCheckout = (domain) => {
    // Use domain-specific dynamic price (3x wholesale) — no hardcoded fallback
    const domainPrice = domain.customer_price || 0;
    const landingAddon = 60; // Matches LANDING_PAGE_ADDON env var
    const finalPrice = selectedPackage === 'domain_and_landing_page'
      ? domainPrice + landingAddon
      : domainPrice;

    const params = new URLSearchParams({
      domain: domain.domain,
      price: finalPrice.toString(),
      customer_price: domainPrice.toString(),
      lead_id: offerData?.offer?.lead?.id || '',
      email: formData.email || '',
      company: formData.company || offerData?.offer?.lead?.company || '',
      industry: formData.industry || offerData?.offer?.lead?.industry || '',
      location: formData.location || offerData?.offer?.lead?.location || '',
      package_type: selectedPackage,
      offer_token: offerData?.offer_token || '',
    });

    if (offerData?.offer?.mockup_path) {
      params.append('mockup', offerData.offer.mockup_path);
    }

    navigate(`/order?${params.toString()}`);
  };

  const variant = formVariant || {
    fields: {
      company: { label: 'Company Name *', placeholder: 'e.g., Nebula AI', required: true, visible: true },
      industry: { label: 'Industry', placeholder: 'Select industry...', visible: true },
      location: { label: 'Location', placeholder: 'e.g., Austin, TX', visible: true },
      email: { label: 'Email (optional)', placeholder: 'your@email.com', required: false, visible: true },
      website: { label: 'Current Website', placeholder: 'e.g., acme.com (optional)', visible: true },
    },
    submitLabel: 'Generate Domain Options',
  };

  return (
    <div className="generate-offer-container generate-offer-container--icp">
      <OfferPageHero
        formData={formData}
        industries={industries}
        formVariant={variant}
        loading={loading}
        onInputChange={handleInputChange}
        onSubmit={handleGenerateOffer}
      />

      <section
        id="offer-form"
        className="offer-form-band"
        aria-label="Build your domain offer"
      >
        <div className="offer-form-band__inner">
          <header className="offer-form-band__header">
            <h2>Generate your domain shortlist</h2>
            <p>
              Share a few details and get registrar-verified options. Add the landing package to ship an ICP-ready page.
            </p>
          </header>

          <form className="offer-form-band__form" onSubmit={handleGenerateOffer}>
            <div className="offer-form-band__row">
              <div className="offer-form-band__group">
                <label htmlFor="band-company">{variant.fields.company.label}</label>
                <input
                  id="band-company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder={variant.fields.company.placeholder}
                  required={variant.fields.company.required !== false}
                />
              </div>

              {variant.fields.industry.visible && (
                <div className="offer-form-band__group">
                  <label htmlFor="band-industry">{variant.fields.industry.label}</label>
                  <select
                    id="band-industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                  >
                    <option value="">{variant.fields.industry.placeholder}</option>
                    {industries.map((industry) => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
              )}

              {variant.fields.location.visible && (
                <div className="offer-form-band__group">
                  <label htmlFor="band-location">{variant.fields.location.label}</label>
                  <input
                    id="band-location"
                    name="location"
                    type="text"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder={variant.fields.location.placeholder}
                  />
                </div>
              )}
            </div>

            <div className="offer-form-band__row offer-form-band__row--with-submit">
              {variant.fields.email.visible && (
                <div className="offer-form-band__group">
                  <label htmlFor="band-email">{variant.fields.email.label}</label>
                  <input
                    id="band-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={variant.fields.email.placeholder}
                    required={!!variant.fields.email.required}
                  />
                </div>
              )}

              {variant.fields.website.visible && (
                <div className="offer-form-band__group">
                  <label htmlFor="band-website">{variant.fields.website.label}</label>
                  <input
                    id="band-website"
                    name="website"
                    type="text"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder={variant.fields.website.placeholder}
                  />
                </div>
              )}

              <button
                type="submit"
                className="offer-form-band__submit"
                disabled={loading}
              >
                {loading
                  ? (<><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Building options...</>)
                  : (<><i className="fa-solid fa-rocket" aria-hidden="true" /> {variant.submitLabel || 'Generate Domain Options'}</>)}
              </button>
            </div>
          </form>
        </div>
      </section>

      <div className="offer-content">
        {error && (
          <div className="alert alert-error">
            <span><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> {error}</span>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <span>{successMessage}</span>
          </div>
        )}

        {offerData?.offer?.errors?.length > 0 && (
          <div className="alert alert-error" role="alert">
            <span>
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />{' '}
              {offerData.offer.errors.join(' ')}
            </span>
          </div>
        )}

        {/* Results Section */}
        {offerData && offerData.offer.candidates.length > 0 && (() => {
          const availableDomains = offerData.offer.candidates.filter(d => d.availability === 'available');
          const sortedDomains = availableDomains.length > 0 ? availableDomains : offerData.offer.candidates;
          const safeIndex = currentDomainIndex % sortedDomains.length;
          const domain = sortedDomains[safeIndex];
          const totalOptions = sortedDomains.length;
          const previewVisible = selectedPackage === 'domain_and_landing_page';
          const { isSupported, profile: activeProfile } = getIndustryProfile(formData.industry);

          const goPrev = () => setCurrentDomainIndex((safeIndex - 1 + totalOptions) % totalOptions);
          const goNext = () => setCurrentDomainIndex((safeIndex + 1) % totalOptions);

          return (
            <div
              className="results-section"
              ref={resultsRef}
              tabIndex={-1}
              onKeyDown={handleGalleryKeyDown}
              aria-roledescription="carousel"
              aria-label={`Domain offer ${safeIndex + 1} of ${totalOptions}`}
            >
              <div className="results-band">
                <div className="results-band__left">
              {/* Gallery header with cycled arrows + dots */}
              <div className="results-nav">
                {totalOptions > 1 && (
                  <button type="button" className="results-nav-arrow" onClick={goPrev} aria-label="Previous domain">
                    <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                  </button>
                )}

                <div className="results-nav-info">
                  <h2>Your Domain Offer</h2>
                  <p className="results-subtitle">
                    {totalOptions > 1
                      ? `${safeIndex + 1} of ${totalOptions} options for ${offerData.offer.lead.company}`
                      : offerData.offer.lead.company}
                  </p>
                </div>

                {totalOptions > 1 && (
                  <button type="button" className="results-nav-arrow" onClick={goNext} aria-label="Next domain">
                    <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* Progress dots */}
              {totalOptions > 1 && (
                <div className="results-dots" role="tablist" aria-label="Domain options">
                  <div className="results-dots-track">
                    {sortedDomains.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        role="tab"
                        aria-selected={i === safeIndex}
                        aria-label={`Domain ${i + 1}`}
                        className={`results-dot${i === safeIndex ? ' is-active' : ''}`}
                        onClick={() => setCurrentDomainIndex(i)}
                      />
                    ))}
                  </div>
                  {/* Progress line */}
                  <div className="results-progress">
                    <div
                      className="results-progress-fill"
                      style={{ width: `${((safeIndex + 1) / totalOptions) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="domain-card domain-card--in-band">
                <div className="domain-card-content">
                  <div className="domain-name">{domain.domain}</div>

                  <div className="domain-details">
                    <div className="detail-row">
                      <span className="label">Availability:</span>
                      <span className={`availability ${domain.availability}`}>
                        {domain.availability === 'unknown' ? 'unverified' : domain.availability}
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="label">Market Value:</span>
                      <span className="cost" style={{ textDecoration: 'line-through', opacity: 0.85, color: '#475569' }}>
                        ${((domain.customer_price || 0) * 1.5).toFixed(0)}
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="label">Your Price:</span>
                      <span className="your-price">
                        ${domain.customer_price?.toFixed(2) || '—'}
                      </span>
                    </div>

                    {domain.score && (
                      <div className="detail-row">
                        <span className="label">Match Score:</span>
                        <span className="score-stars">
                          {domain.score >= 1.5 ? (
                            <>
                              <i className="fa-solid fa-star"></i>
                              <i className="fa-solid fa-star"></i>
                              <i className="fa-solid fa-star"></i>
                              <span className="score-label">Excellent</span>
                            </>
                          ) : domain.score >= 1.0 ? (
                            <>
                              <i className="fa-solid fa-star"></i>
                              <i className="fa-solid fa-star"></i>
                              <i className="fa-regular fa-star"></i>
                              <span className="score-label">Great</span>
                            </>
                          ) : domain.score >= 0.5 ? (
                            <>
                              <i className="fa-solid fa-star"></i>
                              <i className="fa-regular fa-star"></i>
                              <i className="fa-regular fa-star"></i>
                              <span className="score-label">Good</span>
                            </>
                          ) : (
                            <>
                              <i className="fa-regular fa-star"></i>
                              <i className="fa-regular fa-star"></i>
                              <i className="fa-regular fa-star"></i>
                              <span className="score-label">Match</span>
                            </>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="pkg-picker">
                    <h3 className="pkg-picker-title">Select Your Package</h3>
                    <PackagePicker selectedPackage={selectedPackage} onSelectPackage={setSelectedPackage} customerPrice={domain.customer_price} />
                  </div>

                  {previewVisible && (
                    <p className="pkg-preview-note">
                      <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />{' '}
                      {isSupported
                        ? `Blurred preview of your ${formData.industry || 'industry'} landing page on ${domain.domain}`
                        : `Generic preview on ${domain.domain} now, with custom design available on demand`}
                    </p>
                  )}

                  <div className="domain-actions">
                    <button className="btn-checkout" onClick={() => handleCheckout(domain)}>
                      Proceed to Checkout (${(selectedPackage === 'domain_and_landing_page' ? (domain.customer_price || 0) + 60 : (domain.customer_price || 0)).toFixed(2)})
                    </button>
                  </div>
                </div>
              </div>

              {formData.email && (
                <div className="email-section">
                  <button className="btn-send-email" onClick={handleSendEmail} disabled={emailSending || emailSent}>{emailSending ? <><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Sending...</> : emailSent ? (emailCooldown > 0 ? `Sent (${emailCooldown}s)` : <><i className="fa-solid fa-circle-check" aria-hidden="true" /> Sent</>) : <><i className="fa-solid fa-envelope" aria-hidden="true" /> Send Offer to Email</>}</button>
                  {emailSent && <p className="email-success"><i className="fa-solid fa-circle-check" aria-hidden="true" /> Sent!</p>}
                  {emailError && <p className="email-error"><i className="fa-solid fa-circle-xmark" aria-hidden="true" /> {emailError}</p>}
                  {!emailSent && !emailError && (
                    <p className="email-note">Send offer to {formData.email}</p>
                  )}
                </div>
              )}
                </div>

                <aside className="results-band__right" aria-label="Live ICP landing template preview">
                  <div className="results-band__preview-shell">
                    {previewVisible ? (
                      <IcpFormPreview
                        domain={domain.domain}
                        company={formData.company}
                        industry={formData.industry}
                        location={formData.location}
                        visible={true}
                        mode="clear"
                        placement="band"
                      />
                    ) : (
                      <div className="results-band__domain-placeholder">
                        <div className="rbd-inner">
                          <div className="rbd-eyebrow">Selected domain</div>
                          <div className="rbd-name">
                            <span className="rbd-name__base">{domain.domain.includes('.') ? domain.domain.split('.').slice(0, -1).join('.') : domain.domain}</span>
                            <span className="rbd-name__ext">{domain.domain.includes('.') ? '.' + domain.domain.split('.').slice(-1)[0] : ''}</span>
                          </div>
                          <ul className="rbd-features">
                            <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> Domain registration</li>
                            <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> DNS provisioning</li>
                            <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> Full ownership transfer</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="results-band__preview-caption">
                    <span className="results-band__preview-label">
                      <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />{' '}
                      Template applied: {isSupported
                        ? (activeProfile?.heroHeading?.replace('{company}', formData.company || 'your brand') || 'ICP-tailored landing page')
                        : 'Generic ICP layout (custom design on demand)'}
                    </span>
                    <span className="results-band__preview-meta">
                      Live preview for {domain.domain}
                      {previewVisible
                        ? ' included in selected package'
                        : ' (add Landing Page package to ship this layout)'}
                    </span>
                  </div>
                </aside>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Trust Section */}
      <div className="trust-section trust-section--icp">
        <h3>Why teams pair domains with our ICP landing templates</h3>
        <div className="trust-items trust-items--two">
          <div className="trust-item">
            <span className="icon"><i className="fa-solid fa-bullseye" aria-hidden="true" /></span>
            <p>
              Registrar-verified domains plus an optional landing add-on built from the same ICP template
              spine you see in preview, tuned for outbound and demo traffic.
            </p>
          </div>
          <div className="trust-item">
            <span className="icon"><i className="fa-solid fa-shield-halved" aria-hidden="true" /></span>
            <p>PayPal checkout and a single clear handoff to provisioning after you pay.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateOffer;
